import React, { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Hours-proofing grid — the in-app version of Foundation's "Employee/Job Hours
// Proofing Report" (employee → job → day), opened from the Field Reports tab
// next to the payroll CSV download and driven by the SAME week picker. Owner +
// office admin only (the API enforces it; the button is gated the same way).
//
// Reads /api/payroll/hours-proofing?week_start=<Monday>. Cells whose hours come
// from a report still PENDING approval render amber — those hours are NOT in
// the payroll CSV until the report is approved. PM-timesheet lines are marked
// "PM". The Print button clones the table into a bare window and prints it
// (inline styles travel with innerHTML, so the printout matches the screen).

const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
function dayLabel(ymd) {
  const [, m, d] = ymd.split('-').map(Number)
  return `${m}/${d}`
}
// Trim trailing zeros: 8 → "8", 3.67 → "3.67". Blank for zero on non-pending
// cells is handled at the call site (a pending 0 must still show, loudly).
function fmtH(h) {
  if (h == null) return ''
  const n = Math.round(Number(h) * 100) / 100
  return Number.isFinite(n) ? String(n) : ''
}
function niceSunday(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

const PENDING_BG = '#fff4e5'
const PENDING_FG = '#92510a'
// Walk-on (unscheduled) hours. DELIBERATELY COMPOSES WITH AMBER RATHER THAN REPLACING IT:
// "pending approval" and "was not on the schedule" are independent facts and a cell can be
// both, so a pending walk-on keeps the amber fill and gains a purple left edge instead of
// one colour overwriting the other.
// A day whose report was REOPENED — i.e. approved, then unlocked and corrected.
// Reopening (owner, or a named office delegate (Carla); a non-owner reopen emails
// the owners) always precedes a correction, so the
// `reopened` entry in admin_actions_history already identifies every corrected
// report; this is the same signal reportAudit.js shows on the report itself.
// ⚠ A MARKER ONLY. It says "this day was changed after approval, check it against
// what was paid" — it does NOT say by how much, and nothing here computes a
// payroll figure. Cornerstone's adjustments are a reviewed manual process; see
// api/lib/out-of-week.js. Rendered as an UNDERLINE so it composes with the amber
// fill and the purple left edge instead of replacing either.
const REOPEN_EDGE = '#1f6fb2'
const REOPEN_FG = '#1a5b93'
const UNSCHED_BG = '#f5f1ff'
const UNSCHED_FG = '#5b3fa8'
const UNSCHED_EDGE = '#7c5cd6'

export default function HoursProofingModal({ weekStart, weekEnding, onClose }) {
  const [phase, setPhase] = useState('loading') // loading | ready | error
  const [errMsg, setErrMsg] = useState('')
  const [data, setData] = useState(null)
  // '' = all employees. Narrows the grid to one man for a signed one-page
  // review; the key matches the row key the grid already uses.
  const [empKey, setEmpKey] = useState('')
  const printRef = useRef(null)

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession()
        const token = s?.access_token
        if (!token) { if (alive) { setErrMsg('Your session expired — please sign in again.'); setPhase('error') } return }
        const res = await fetch(`/api/payroll/hours-proofing?week_start=${weekStart}`, { headers: { Authorization: `Bearer ${token}` } })
        const body = await res.json().catch(() => ({}))
        if (!alive) return
        if (!res.ok || !body.ok) {
          setErrMsg(res.status === 403 ? 'You don’t have access to payroll hours.' : 'Could not load the hours for this week. Please try again.')
          setPhase('error')
          return
        }
        setData(body)
        setEmpKey('')   // a new week may not contain the previously picked man
        setPhase('ready')
      } catch (e) {
        if (alive) { setErrMsg('Could not load the hours for this week. Please try again.'); setPhase('error') }
      }
    })()
    return () => { alive = false }
  }, [weekStart])

  // The grid's own row key, reused as the picker's value so the two can't drift.
  const keyOf = (emp) => `${emp.emp_number}|${emp.name}`
  const allEmployees = (data && data.employees) || []
  const employeesShown = empKey ? allEmployees.filter(e => keyOf(e) === empKey) : allEmployees
  const pickedName = empKey ? (employeesShown[0] || {}).name || '' : ''
  // ⚠ WEEK-LEVEL AGGREGATES ARE HIDDEN WHILE FILTERED. Job Summary and Grand
  // total describe the WHOLE week; leaving them under a one-man grid — on a
  // sheet that gets printed and signed — would read as that man's totals. His
  // own subtotal row is untouched and still shown.
  const showWeekAggregates = !empKey

  // UNLINKED-WORKER HARD STOP. `unlinked` comes from the server with the SAME
  // employee resolver the payroll CSV uses: pending:false lines are on approved
  // reports and BLOCK the CSV download right now; pending:true lines will block
  // that report's approval.
  const unlinkedAll = (data && Array.isArray(data.unlinked)) ? data.unlinked : []
  const unlinkedBlocking = unlinkedAll.filter(u => !u.pending)
  const unlinkedPending = unlinkedAll.filter(u => u.pending)
  const unlinkedLine = (u) => `${u.name} — job ${u.job_number || '?'} ${u.date} (report #${u.report_id ?? '?'}, ${u.hours}h)`
  const escHtml = (t) => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  function unlinkedPrintHtml() {
    if (!unlinkedAll.length) return ''
    const parts = []
    if (unlinkedBlocking.length) parts.push(`<div><strong>Payroll CSV is BLOCKED — ${unlinkedBlocking.length} worker line${unlinkedBlocking.length === 1 ? '' : 's'} ${unlinkedBlocking.length === 1 ? 'has' : 'have'} no employee number:</strong> ${escHtml(unlinkedBlocking.map(unlinkedLine).join('; '))}</div>`)
    if (unlinkedPending.length) parts.push(`<div><strong>Not linked yet (pending reports — approval is blocked until linked):</strong> ${escHtml(unlinkedPending.map(unlinkedLine).join('; '))}</div>`)
    return `<div style="background:#fdecec;border-left:4px solid #d9534f;color:#a12622;padding:8px 12px;margin-bottom:12px;font-size:12px;line-height:1.5">${parts.join('')}</div>`
  }

  function printGrid() {
    if (!printRef.current || !data) return
    const w = window.open('', '_blank')
    if (!w) return
    const who = pickedName ? `${pickedName} — ` : ''
    w.document.write(`<!DOCTYPE html><html><head><title>Hours proofing — ${who}week ending ${niceSunday(data.week_end)}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#1f2430}
      table{border-collapse:collapse}
      /* Browsers strip background fills when printing unless told not to — without
         this the printed sheet loses BOTH the amber pending cells and the purple
         walk-on cells, which are the whole point of proofing off paper. */
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @media print { * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      @media print { body{margin:8mm} }</style></head><body>
      <div style="font-size:16px;font-weight:700;margin-bottom:2px">Cornerstone Contracting — Hours Proofing</div>
      ${pickedName ? `<div style="font-size:14px;font-weight:700;margin-bottom:2px">${pickedName}</div>` : ''}
      <div style="font-size:12px;color:#555;margin-bottom:14px">Week ${data.week_start} to ${data.week_end} (week ending ${niceSunday(data.week_end)}) — amber = pending approval, not yet in the payroll CSV · purple = unscheduled (walk-on) hours · blue underline = day from a report that was reopened and corrected after approval</div>
      ${unlinkedPrintHtml()}
      ${printRef.current.innerHTML}
      </body></html>`)
    w.document.close()
    w.focus()
    setTimeout(() => { try { w.print() } catch (e) { /* user can print manually */ } }, 250)
  }

  const th = { padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#4a5568', textAlign: 'right', borderBottom: '2px solid #cfd6dd', whiteSpace: 'nowrap', background: '#f7f9fb', position: 'sticky', top: 0 }
  const thL = { ...th, textAlign: 'left' }
  const td = { padding: '4px 8px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f2f4', fontVariantNumeric: 'tabular-nums' }
  const tdL = { ...td, textAlign: 'left' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1060, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.25)', overflow: 'hidden' }}>

        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #eef0f2', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1e2d3d' }}>Hours proofing — week ending {niceSunday(weekEnding)}</div>
            {phase === 'ready' && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {data.counts.total_reports} report{data.counts.total_reports === 1 ? '' : 's'} · {data.counts.approved} approved · {data.counts.pending} pending · {data.pm_timesheets.submittedCount} PM timesheet{data.pm_timesheets.submittedCount === 1 ? '' : 's'} submitted
                {data.counts.unscheduled_hours > 0 ? ` · ${data.counts.unscheduled_hours} unscheduled hrs` : ''}
              </div>
            )}
            {phase === 'ready' && (
              <div style={{ fontSize: 11, color: '#8a93a0', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span><span style={{ background: PENDING_BG, color: PENDING_FG, padding: '0 5px', borderRadius: 3 }}>amber</span> pending approval</span>
                <span><span style={{ background: UNSCHED_BG, color: UNSCHED_FG, padding: '0 5px', borderRadius: 3, boxShadow: `inset 3px 0 0 ${UNSCHED_EDGE}` }}>purple</span> walk-on</span>
                <span><span style={{ color: REOPEN_FG, borderBottom: `2px solid ${REOPEN_EDGE}`, padding: '0 3px' }}>underlined</span> reopened &amp; corrected after approval</span>
              </div>
            )}
          </div>
          {phase === 'ready' && allEmployees.length > 0 && (
            <select value={empKey} onChange={e => setEmpKey(e.target.value)}
              title="Narrow the grid to one employee"
              style={{ padding: '7px 10px', fontSize: 13, border: '1px solid #cfd6dd', borderRadius: 8, background: '#fff', color: '#1e2d3d', maxWidth: 260 }}>
              <option value="">All employees ({allEmployees.length})</option>
              {allEmployees.map(e => (
                <option key={keyOf(e)} value={keyOf(e)}>
                  {e.emp_number ? `${e.emp_number} — ` : ''}{e.name}
                </option>
              ))}
            </select>
          )}
          {phase === 'ready' && (
            <button onClick={printGrid}
              style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, border: '1px solid #cfd6dd', borderRadius: 8, background: '#fff', color: '#1e2d3d', cursor: 'pointer' }}>
              🖨 Print
            </button>
          )}
          <button onClick={onClose}
            style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: '#1e2d3d', color: '#fff', cursor: 'pointer' }}>
            Close
          </button>
        </div>

        {phase === 'loading' && (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Adding up the week’s hours…</div>
        )}
        {phase === 'error' && (
          <div style={{ margin: 20, background: '#fdecec', borderLeft: '4px solid #d9534f', color: '#a12622', padding: '10px 12px', borderRadius: '0 6px 6px 0', fontSize: 13 }}>{errMsg}</div>
        )}

        {phase === 'ready' && (
          <>
            {unlinkedAll.length > 0 && (
              <div style={{ margin: '12px 20px 0', background: '#fdecec', borderLeft: '4px solid #d9534f', color: '#a12622', padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 12, lineHeight: 1.5 }}>
                {unlinkedBlocking.length > 0 && (
                  <div><strong>Payroll CSV is BLOCKED — {unlinkedBlocking.length} worker line{unlinkedBlocking.length === 1 ? '' : 's'} {unlinkedBlocking.length === 1 ? 'has' : 'have'} no employee number:</strong> {unlinkedBlocking.map(unlinkedLine).join('; ')}</div>
                )}
                {unlinkedPending.length > 0 && (
                  <div><strong>Not linked yet (pending reports — approval is blocked until linked):</strong> {unlinkedPending.map(unlinkedLine).join('; ')}</div>
                )}
                <div style={{ marginTop: 2 }}>Open the report and use <strong>Link to employee</strong>. Not in Foundation yet? Add them, run the Foundation sync (Admin tab), then link.</div>
              </div>
            )}
            {(data.late_entries || []).length > 0 && (
              <div style={{ margin: '12px 20px 0', background: '#fdecec', borderLeft: '4px solid #d9534f', color: '#a12622', padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 12, lineHeight: 1.5 }}>
                <strong>{data.late_entries.length} report{data.late_entries.length === 1 ? '' : 's'} in this week {data.late_entries.length === 1 ? 'was' : 'were'} entered after the week closed:</strong>{' '}
                {data.late_entries.map(l => `${l.job_number || l.job_name || 'Job'}${l.job_number && l.job_name ? ` ${l.job_name}` : ''} ${l.date}${l.by ? ` (${l.by})` : ''}`).join('; ')}
              </div>
            )}

            {(data.counts.pending > 0 || data.pm_timesheets.missing.length > 0) && (
              <div style={{ margin: '12px 20px 0', background: PENDING_BG, borderLeft: '4px solid #e8820c', color: PENDING_FG, padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 12, lineHeight: 1.5 }}>
                {data.counts.pending > 0 && (
                  <div><strong>{data.counts.pending} report{data.counts.pending === 1 ? '' : 's'} pending approval</strong> — amber hours below are not in the payroll CSV yet: {data.pending_reports.map(p => `${p.job_name || p.job_number || 'Job'} ${p.date}${p.foreman_name ? ` (${p.foreman_name})` : ''}`).join('; ')}</div>
                )}
                {data.pm_timesheets.missing.length > 0 && (
                  <div><strong>PM timesheets not submitted:</strong> {data.pm_timesheets.missing.map(p => p.name).join(', ')}</div>
                )}
              </div>
            )}

            {data.counts.unscheduled_hours > 0 && (
              <div style={{ margin: '8px 20px 0', background: UNSCHED_BG, borderLeft: `4px solid ${UNSCHED_EDGE}`, color: UNSCHED_FG, padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 12, lineHeight: 1.5 }}>
                <strong>{data.counts.unscheduled_hours} unscheduled hours this week</strong> — these workers were not on the schedule for that job: {(data.unscheduled_list || []).map(u => `${u.name} — ${u.job_number || u.job_name || 'Job'}${u.job_number && u.job_name ? ` ${u.job_name}` : ''} (${u.hours}h)`).join('; ')}
              </div>
            )}

            <div style={{ overflow: 'auto', padding: '12px 20px 20px' }} ref={printRef}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ ...thL, minWidth: 46 }}>Emp #</th>
                    <th style={{ ...thL, minWidth: 210 }}>Employee / Job</th>
                    <th style={thL}>Type</th>
                    {data.days.map((d, i) => (
                      <th key={d} style={th}>{dayNames[i]}<br />{dayLabel(d)}</th>
                    ))}
                    <th style={th}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {employeesShown.map(emp => (
                    <React.Fragment key={`${emp.emp_number}|${emp.name}`}>
                      <tr>
                        <td style={{ ...tdL, fontWeight: 700, borderBottom: 'none', paddingTop: 10 }}>{emp.emp_number}</td>
                        <td colSpan={10} style={{ ...tdL, fontWeight: 700, borderBottom: 'none', paddingTop: 10 }}>
                          {emp.name}
                          {emp.trade ? <span style={{ fontWeight: 400, color: '#6b7280' }}> · {emp.trade}</span> : null}
                          {emp.emp_number === '' ? <span style={{ fontWeight: 600, color: '#a12622', fontSize: 11 }}> · not on roster</span> : null}
                          {emp.pending ? <span style={{ fontWeight: 600, color: PENDING_FG, fontSize: 11 }}> · has pending hours</span> : null}
                          {emp.unscheduled ? <span style={{ fontWeight: 600, color: UNSCHED_FG, fontSize: 11 }}> · has unscheduled hours</span> : null}
                          {emp.reopened ? <span style={{ fontWeight: 600, color: REOPEN_FG, fontSize: 11 }}> · has corrected days</span> : null}
                        </td>
                      </tr>
                      {emp.lines.map((l, li) => (
                        <tr key={li}>
                          <td style={td}></td>
                          <td style={{ ...tdL, paddingLeft: 20, color: '#374151' }}>
                            {(l.job_number ? `${l.job_number} — ` : '') + (l.job_name || '(no job)')}
                            {l.source === 'pm' ? <span style={{ color: '#1f4e79', fontSize: 10, fontWeight: 700 }}> PM</span> : null}
                            {l.unscheduled ? <span style={{ color: UNSCHED_FG, fontSize: 10, fontWeight: 700 }}> WALK-ON</span> : null}
                          </td>
                          <td style={{ ...tdL, color: '#6b7280' }} title={l.cost_codes.length ? `Cost code${l.cost_codes.length === 1 ? '' : 's'}: ${l.cost_codes.join(', ')}` : undefined}>{l.rate}</td>
                          {data.days.map(d => {
                            const pending = !!l.pending_days[d]
                            const unsched = !!l.unscheduled
                            const reopened = !!(l.reopened_days && l.reopened_days[d])
                            const has = l.days[d] != null
                            // Both signals must stay readable when they coincide: amber keeps
                            // the fill (it is the payroll-blocking one), purple takes the edge.
                            const cellStyle = pending && unsched
                              ? { background: PENDING_BG, color: PENDING_FG, fontWeight: 600, boxShadow: `inset 3px 0 0 ${UNSCHED_EDGE}` }
                              : pending ? { background: PENDING_BG, color: PENDING_FG, fontWeight: 600 }
                              : unsched ? { background: UNSCHED_BG, color: UNSCHED_FG, fontWeight: 600 }
                              : {}
                            const cellTitle = pending && unsched
                              ? 'Unscheduled (walk-on) hours — not on the schedule for this day — AND from a report pending approval, so not in the payroll CSV yet'
                              : pending ? 'From a report pending approval — not in the payroll CSV yet'
                              : unsched ? 'Unscheduled (walk-on) hours — not on the schedule for this day'
                              : undefined
                            // Three independent facts can coincide on one cell, so each takes a
                            // different channel: amber the FILL, purple the LEFT EDGE, reopened
                            // the BOTTOM BORDER. None of them can hide another.
                            const reopenStyle = reopened ? { borderBottom: `2px solid ${REOPEN_EDGE}` } : {}
                            const reopenTitle = reopened
                              ? 'This day came from a report that was reopened and corrected after approval — check it against what was already paid'
                              : ''
                            const fullTitle = [cellTitle, reopenTitle].filter(Boolean).join(' · ') || undefined
                            return (
                              <td key={d} style={{ ...td, ...cellStyle, ...reopenStyle }} title={fullTitle}>
                                {has ? fmtH(l.days[d]) : ''}
                              </td>
                            )
                          })}
                          <td style={{ ...td, fontWeight: 600 }}>{fmtH(l.total)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td style={td}></td>
                        <td style={{ ...tdL, paddingLeft: 20, color: '#6b7280', fontSize: 11, borderBottom: '2px solid #e2e5e9' }}>Employee total</td>
                        <td style={{ ...td, borderBottom: '2px solid #e2e5e9' }}></td>
                        {data.days.map(d => (
                          <td key={d} style={{ ...td, fontWeight: 700, background: '#f7f9fb', borderBottom: '2px solid #e2e5e9' }}>{emp.day_totals[d] != null ? fmtH(emp.day_totals[d]) : ''}</td>
                        ))}
                        <td style={{ ...td, fontWeight: 700, background: '#f7f9fb', borderBottom: '2px solid #e2e5e9' }}>{fmtH(emp.total)}</td>
                      </tr>
                    </React.Fragment>
                  ))}

                  {showWeekAggregates && (
                  <tr>
                    <td colSpan={11} style={{ ...tdL, fontWeight: 700, paddingTop: 16, borderBottom: '2px solid #cfd6dd', color: '#1e2d3d' }}>Job Summary</td>
                  </tr>
                  )}
                  {showWeekAggregates && data.job_summary.map((j, ji) => (
                    <tr key={ji}>
                      <td style={td}></td>
                      <td style={{ ...tdL, color: '#374151' }}>
                        {(j.job_number ? `${j.job_number} — ` : '') + (j.job_name || '(no job)')}
                        {j.pending ? <span style={{ color: PENDING_FG, fontSize: 10, fontWeight: 700 }}> ⚠</span> : null}
                        {j.unscheduled ? <span style={{ color: UNSCHED_FG, fontSize: 10, fontWeight: 700 }}> ✚</span> : null}
                      </td>
                      <td style={{ ...tdL, color: '#6b7280' }}>{j.rate}</td>
                      {data.days.map(d => (
                        <td key={d} style={td}>{j.days[d] != null ? fmtH(j.days[d]) : ''}</td>
                      ))}
                      <td style={{ ...td, fontWeight: 600 }}>{fmtH(j.total)}</td>
                    </tr>
                  ))}
                  {showWeekAggregates && (
                  <tr>
                    <td style={{ ...td, borderBottom: 'none' }}></td>
                    <td style={{ ...tdL, fontWeight: 700, borderBottom: 'none' }}>Grand total</td>
                    <td style={{ ...td, borderBottom: 'none' }}></td>
                    {data.days.map(d => (
                      <td key={d} style={{ ...td, fontWeight: 700, background: '#eef3fb', borderBottom: 'none' }}>{data.day_grand[d] != null ? fmtH(data.day_grand[d]) : ''}</td>
                    ))}
                    <td style={{ ...td, fontWeight: 800, background: '#eef3fb', borderBottom: 'none' }}>{fmtH(data.grand_total)}</td>
                  </tr>
                  )}
                </tbody>
              </table>
              {employeesShown.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{empKey ? 'No hours recorded for that employee this week.' : 'No hours recorded for this week yet.'}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
